import React, { useEffect, useState, useCallback } from "react";
import { Icon } from '@iconify/react';
import { Link, NavLink, useLocation } from "react-router-dom";
import config from "../config";
import { useAuth } from "../context/AuthContext";
import { applicationTabs } from "../helper/applicationTabs";
import { useApplicationContext } from "../context/ApplicationContext";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import { applyTheme, initTheme } from "../helper/themeHelper";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const MasterLayout = ({ children }) => {
  let [sidebarActive, seSidebarActive] = useState(localStorage.getItem('sidebarActive') === 'true');
  const { activeTabId, setActiveTabId } = useApplicationContext();
  let [mobileMenu, setMobileMenu] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const { auth, user } = useAuth();
  const [hasSubscription, setHasSubscription] = useState(false);
  const APP_TYPE = process.env.REACT_APP_APP_TYPE || 'dev';

  // SuperAdmin Sidebar Notification Badges (Page 26)
  const [badgeCounts, setBadgeCounts] = useState({
    inquiries: 0,
    support_tickets: 0,
    venue_verification: 0,
    subscriptions: 0,
    moderation: 0
  });

  const fetchBadgeCounts = useCallback(async () => {
    if (!user || user.role !== 'admin') return;
    try {
      const response = await auth.fetch(`${config.API_BASE_URL}/api/stats/badge-counts`);
      if (response.ok) {
        const data = await response.json();
        setBadgeCounts({
          inquiries: data.inquiries || 0,
          support_tickets: data.support_tickets || 0,
          venue_verification: data.venue_verification || 0,
          subscriptions: data.subscriptions || 0,
          moderation: data.moderation || 0
        });
      }
    } catch (error) {
      console.warn('Failed to fetch badge counts:', error);
    }
  }, [user, auth]);

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchBadgeCounts();
      const interval = setInterval(fetchBadgeCounts, 60000);
      return () => clearInterval(interval);
    }
  }, [user, fetchBadgeCounts]);

  // Check subscription status
  useEffect(() => {
    const checkSubscription = async () => {
        if (!user) return;
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/payments/subscription`);
            if (response.ok) {
                const data = await response.json();
                setHasSubscription(!!data.subscription);
            }
        } catch (error) {
            console.error('Failed to check subscription:', error);
        }
    };

    checkSubscription();
  }, [user, auth]);

  // Fetch notifications logic
  const fetchNotifications = useCallback(async () => {
    try {
        const response = await auth.fetch(`${config.API_BASE_URL}/api/notifications`);
        
        if (response.ok) {
            const data = await response.json();
            setNotifications(data.notifications || []);
            setUnreadCount(data.unreadCount || 0);
        }
    } catch (error) {
        console.error('Failed to fetch notifications', error);
    }
  }, [auth]);

  useEffect(() => {
    if (user) {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 60000);
        return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  const handleMarkAllRead = async () => {
    try {
        await auth.fetch(`${config.API_BASE_URL}/api/notifications/read-all`, {
            method: 'PUT'
        });
        fetchNotifications();
    } catch (error) {
        console.error('Failed to mark all read', error);
    }
  };

  // Initialize theme from local storage on mount
  useEffect(() => {
    initTheme();
  }, []);

  useEffect(() => {
    fetch(`${config.API_BASE_URL}/api/settings`)
      .then(res => res.json())
      .then(data => {
        if (data.logo_url) {
           const url = data.logo_url.startsWith('http') ? data.logo_url : `${config.API_BASE_URL}${data.logo_url}`;
           setLogoUrl(url);
        }
        if (data.site_title) {
          window.__SITE_TITLE__ = data.site_title;
        }
        if (data.theme_color) {
            applyTheme(data.theme_color);
        }
      })
      .catch(err => console.error('Failed to fetch settings:', err));

    const handleDropdownClick = (event) => {
      event.preventDefault();
      const clickedLink = event.currentTarget;
      const clickedDropdown = clickedLink.closest(".dropdown");

      if (!clickedDropdown) return;

      const isActive = clickedDropdown.classList.contains("open");

      // Close all dropdowns
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        dropdown.classList.remove("open");
        const submenu = dropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = "0px";
        }
      });

      // Toggle the clicked dropdown
      if (!isActive) {
        clickedDropdown.classList.add("open");
        const submenu = clickedDropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = `${submenu.scrollHeight}px`;
        }
      }
    };

    const dropdownTriggers = document.querySelectorAll(
      ".sidebar-menu .dropdown > a, .sidebar-menu .dropdown > Link"
    );

    dropdownTriggers.forEach((trigger) => {
      trigger.addEventListener("click", handleDropdownClick);
    });

    const openActiveDropdown = () => {
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        const submenuLinks = dropdown.querySelectorAll(".sidebar-submenu li a");
        submenuLinks.forEach((link) => {
          if (
            link.classList.contains("active-page") ||
            link.getAttribute("href") === location.pathname ||
            link.getAttribute("to") === location.pathname
          ) {
            dropdown.classList.add("open");
            const submenu = dropdown.querySelector(".sidebar-submenu");
            if (submenu) {
              submenu.style.maxHeight = `${submenu.scrollHeight}px`;
            }
          }
        });
      });
    };

    openActiveDropdown();

    return () => {
      dropdownTriggers.forEach((trigger) => {
        trigger.removeEventListener("click", handleDropdownClick);
      });
    };
  }, [location.pathname, hasSubscription]);

  let sidebarControl = () => {
    const newState = !sidebarActive;
    seSidebarActive(newState);
    localStorage.setItem('sidebarActive', newState);
  };

  let mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  const isMyVenuesChildRoute =
    location.pathname === '/my-venues' ||
    location.pathname === '/applications/my-venues' ||
    location.pathname.startsWith('/venue/') ||
    location.pathname.startsWith('/edit-venue/');

  const isVenuesGroupActive =
    isMyVenuesChildRoute ||
    location.pathname === '/add-venue' ||
    location.pathname === '/reservations';

  return (
    <section className={mobileMenu ? "overlay active" : "overlay "}>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      {/* sidebar */}
      <aside
        className={
          sidebarActive
            ? "sidebar active "
            : mobileMenu
            ? "sidebar sidebar-open"
            : "sidebar"
        }
      >
        <button
          onClick={mobileMenuControl}
          type='button'
          className='sidebar-close-btn'
          aria-label='Close sidebar'
        >
          <Icon icon='radix-icons:cross-2' />
        </button>
        <div>
          <Link to='/' className='sidebar-logo'>
            <img
              src={logoUrl || '/assets/images/logo.png'}
              alt='HubScroll logo'
              className='light-logo'
              onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/logo.png'; }}
            />
            <img
              src={logoUrl || '/assets/images/logo-light.png'}
              alt='HubScroll logo'
              className='dark-logo'
              onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/logo-light.png'; }}
            />
            <img
              src={logoUrl || '/assets/images/logo-icon.png'}
              alt='HubScroll icon'
              className='logo-icon'
              onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/logo-icon.png'; }}
            />
            {!sidebarActive && (
              <span className="ms-2 fw-bold" style={{
                background: 'linear-gradient(270deg, #24c5f7 0%, #0388d6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: '22px',
                lineHeight: '1',
                display: 'inline-block'
              }}>
                HubScroll
              </span>
            )}
          </Link>
        </div>
        <div className='sidebar-menu-area'>
          <ul className='sidebar-menu' id='sidebar-menu'>
            {user?.role === 'admin' ? (
              <>
                {/* ============================================================ */}
                {/* 1. DASHBOARD                                                 */}
                {/* ============================================================ */}
                <li className='sidebar-menu-group-title'>1. Dashboard</li>
                <li>
                  <NavLink
                    to='/control-tower'
                    className={(navData) => navData.isActive ? "active-page" : ""}
                  >
                    <Icon icon='solar:home-smile-angle-outline' className='menu-icon' />
                    <span>Business Overview</span>
                  </NavLink>
                </li>

                {/* ============================================================ */}
                {/* 2. ANALYTICS                                                 */}
                {/* ============================================================ */}
                <li className='sidebar-menu-group-title'>2. Analytics</li>
                <li>
                  <NavLink
                    to='/index-1'
                    className={(navData) => navData.isActive ? "active-page" : ""}
                  >
                    <Icon icon='solar:chart-square-outline' className='menu-icon' />
                    <span>Platform Analytics</span>
                  </NavLink>
                </li>
                <li>
                  <Link
                    to='/applications/vendor-analytics'
                    onClick={() => setActiveTabId('vendor-analytics')}
                    className={
                      location.pathname === '/applications/vendor-analytics' ||
                      (location.pathname === '/applications' && activeTabId === 'vendor-analytics')
                        ? "active-page"
                        : ""
                    }
                  >
                    <Icon icon='solar:chart-2-outline' className='menu-icon' />
                    <span>Venue Analytics</span>
                  </Link>
                </li>

                {/* ============================================================ */}
                {/* 3. OPERATIONS                                                */}
                {/* ============================================================ */}
                <li className='sidebar-menu-group-title'>3. Operations</li>
                <li>
                  <Link
                    to='/applications/venue-verification'
                    onClick={() => setActiveTabId('venue-verification')}
                    className={
                      location.pathname === '/applications/venue-verification' ||
                      (location.pathname === '/applications' && activeTabId === 'venue-verification')
                        ? "active-page"
                        : ""
                    }
                  >
                    <Icon icon='solar:shield-check-outline' className='menu-icon' />
                    <span>Venue Verification</span>
                    {badgeCounts.venue_verification > 0 && (
                      <span className='badge rounded-pill bg-danger ms-auto' style={{ fontSize: '11px', padding: '3px 8px' }}>
                        {badgeCounts.venue_verification}
                      </span>
                    )}
                  </Link>
                </li>
                <li>
                  <NavLink
                    to='/reservations'
                    className={(navData) => navData.isActive ? "active-page" : ""}
                  >
                    <Icon icon='solar:calendar-date-outline' className='menu-icon' />
                    <span>Reservations</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to='/contact-messages'
                    className={(navData) => navData.isActive ? "active-page" : ""}
                  >
                    <Icon icon='solar:chat-line-outline' className='menu-icon' />
                    <span>Business Inquiries</span>
                    {badgeCounts.inquiries > 0 && (
                      <span className='badge rounded-pill bg-danger ms-auto' style={{ fontSize: '11px', padding: '3px 8px' }}>
                        {badgeCounts.inquiries}
                      </span>
                    )}
                  </NavLink>
                </li>
                <li>
                  <Link
                    to='/applications/moderation'
                    onClick={() => setActiveTabId('moderation')}
                    className={
                      location.pathname === '/applications/moderation' ||
                      (location.pathname === '/applications' && activeTabId === 'moderation')
                        ? "active-page"
                        : ""
                    }
                  >
                    <Icon icon='solar:star-outline' className='menu-icon' />
                    <span>Reviews & Moderation</span>
                    {badgeCounts.moderation > 0 && (
                      <span className='badge rounded-pill bg-danger ms-auto' style={{ fontSize: '11px', padding: '3px 8px' }}>
                        {badgeCounts.moderation}
                      </span>
                    )}
                  </Link>
                </li>

                {/* ============================================================ */}
                {/* 4. MANAGEMENT                                                */}
                {/* ============================================================ */}
                <li className='sidebar-menu-group-title'>4. Management</li>
                <li className='dropdown'>
                  <Link to='#'>
                    <Icon icon='solar:smartphone-outline' className='menu-icon' />
                    <span>Mobile App</span>
                    {badgeCounts.support_tickets > 0 && (
                      <span className='badge rounded-pill bg-danger ms-auto' style={{ fontSize: '11px', padding: '3px 8px', marginRight: '26px' }}>
                        {badgeCounts.support_tickets}
                      </span>
                    )}
                  </Link>
                  <ul className='sidebar-submenu'>
                    <li>
                      <NavLink
                        to='/mobile-content'
                        className={(navData) => navData.isActive ? "active-page" : ""}
                      >
                        Content & FAQs
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to='/support-tickets'
                        className={(navData) => navData.isActive ? "active-page" : ""}
                      >
                        <span>Support Tickets</span>
                        {badgeCounts.support_tickets > 0 && (
                          <span className='badge rounded-pill bg-danger ms-auto' style={{ fontSize: '11px', padding: '3px 8px' }}>
                            {badgeCounts.support_tickets}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  </ul>
                </li>
                <li>
                  <Link
                    to='/applications/categories'
                    onClick={() => setActiveTabId('categories')}
                    className={
                      location.pathname === '/applications/categories' ||
                      (location.pathname === '/applications' && activeTabId === 'categories')
                        ? "active-page"
                        : ""
                    }
                  >
                    <Icon icon='solar:widget-outline' className='menu-icon' />
                    <span>Categories</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to='/applications/all-venues'
                    onClick={() => setActiveTabId('all-venues')}
                    className={
                      location.pathname === '/applications/all-venues' ||
                      (location.pathname === '/applications' && activeTabId === 'all-venues')
                        ? "active-page"
                        : ""
                    }
                  >
                    <Icon icon='solar:buildings-3-outline' className='menu-icon' />
                    <span>All Venues</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to='/applications/subscriptions'
                    onClick={() => setActiveTabId('subscriptions')}
                    className={
                      location.pathname === '/applications/subscriptions' ||
                      (location.pathname === '/applications' && activeTabId === 'subscriptions')
                        ? "active-page"
                        : ""
                    }
                  >
                    <Icon icon='solar:wallet-money-outline' className='menu-icon' />
                    <span>Subscriptions</span>
                    {badgeCounts.subscriptions > 0 && (
                      <span className='badge rounded-pill bg-danger ms-auto' style={{ fontSize: '11px', padding: '3px 8px' }}>
                        {badgeCounts.subscriptions}
                      </span>
                    )}
                  </Link>
                </li>
                <li>
                  <Link
                    to='/applications/user-management'
                    onClick={() => setActiveTabId('user-management')}
                    className={
                      location.pathname === '/applications/user-management' ||
                      (location.pathname === '/applications' && activeTabId === 'user-management')
                        ? "active-page"
                        : ""
                    }
                  >
                    <Icon icon='solar:users-group-rounded-outline' className='menu-icon' />
                    <span>User Management</span>
                  </Link>
                </li>
                <li>
                  <NavLink
                    to='/maps'
                    className={(navData) => navData.isActive ? "active-page" : ""}
                  >
                    <Icon icon='material-symbols:map-outline' className='menu-icon' />
                    <span>Venue Map</span>
                  </NavLink>
                </li>

                {/* ============================================================ */}
                {/* 5. ADMINISTRATION                                            */}
                {/* ============================================================ */}
                <li className='sidebar-menu-group-title'>5. Administration</li>
                <li>
                  <Link
                    to='/applications/employees'
                    onClick={() => setActiveTabId('employees')}
                    className={
                      location.pathname === '/applications/employees' ||
                      (location.pathname === '/applications' && activeTabId === 'employees') ||
                      location.pathname === '/employee-management'
                        ? "active-page"
                        : ""
                    }
                  >
                    <Icon icon='solar:users-group-two-rounded-outline' className='menu-icon' />
                    <span>Staff</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to='/applications/positions'
                    onClick={() => setActiveTabId('positions')}
                    className={
                      location.pathname === '/applications/positions' ||
                      (location.pathname === '/applications' && activeTabId === 'positions') ||
                      location.pathname === '/position-management'
                        ? "active-page"
                        : ""
                    }
                  >
                    <Icon icon='solar:shield-user-outline' className='menu-icon' />
                    <span>Roles & Permissions</span>
                  </Link>
                </li>
                <li>
                  <NavLink
                    to='/audit-logs'
                    className={(navData) => navData.isActive ? "active-page" : ""}
                  >
                    <Icon icon='solar:history-outline' className='menu-icon' />
                    <span>Audit Log</span>
                  </NavLink>
                </li>

                {/* ============================================================ */}
                {/* 6. ACCOUNT                                                   */}
                {/* ============================================================ */}
                <li className='sidebar-menu-group-title'>6. Account</li>
                <li>
                  <NavLink
                    to='/view-profile'
                    className={(navData) => navData.isActive || location.pathname.startsWith('/view-profile') ? "active-page" : ""}
                  >
                    <Icon icon='solar:user-circle-outline' className='menu-icon' />
                    <span>My Profile</span>
                  </NavLink>
                </li>
                <li className='dropdown'>
                  <Link to='#'>
                    <Icon icon='icon-park-outline:setting-two' className='menu-icon' />
                    <span>Settings</span>
                  </Link>
                  <ul className='sidebar-submenu'>
                    <li>
                      <NavLink
                        to='/notification'
                        className={(navData) => navData.isActive ? "active-page" : ""}
                      >
                        Notifications
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to='/theme'
                        className={(navData) => navData.isActive ? "active-page" : ""}
                      >
                        Website Settings
                      </NavLink>
                    </li>
                  </ul>
                </li>

                {/* ============================================================ */}
                {/* 7. LEGAL                                                     */}
                {/* ============================================================ */}
                <li className='sidebar-menu-group-title'>7. Legal</li>
                <li>
                  <NavLink
                    to='/tos'
                    className={(navData) => navData.isActive ? "active-page" : ""}
                  >
                    <Icon icon='solar:document-text-outline' className='menu-icon' />
                    <span>Terms of Service</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to='/privacy'
                    className={(navData) => navData.isActive ? "active-page" : ""}
                  >
                    <Icon icon='solar:shield-check-outline' className='menu-icon' />
                    <span>Privacy Policy</span>
                  </NavLink>
                </li>
              </>
            ) : (
              <>
                {/* Business Client Sidebar */}
                <li className='sidebar-menu-group-title'>Management</li>
                <li>
                  <Link
                    to='/applications/overview'
                    onClick={() => setActiveTabId('overview')}
                    className={
                      location.pathname === '/applications/overview' ||
                      (location.pathname === '/applications' && activeTabId === 'overview')
                        ? "active-page"
                        : ""
                    }
                  >
                    <Icon icon='solar:home-smile-angle-outline' className='menu-icon' />
                    <span>Business Overview</span>
                  </Link>
                </li>

                <li className={`dropdown ${isVenuesGroupActive ? 'open' : ''}`}>
                  <Link to='#' className={isVenuesGroupActive ? 'active-page' : ''}>
                    <Icon icon='solar:shop-2-linear' className='menu-icon' />
                    <span>Venues</span>
                  </Link>
                  <ul className='sidebar-submenu'>
                    <li>
                      <NavLink
                        to='/my-venues'
                        className={() => isMyVenuesChildRoute ? "active-page" : ""}
                      >
                        My Venues
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to='/add-venue'
                        className={(navData) => navData.isActive ? "active-page" : ""}
                      >
                        Add New Venue
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to='/reservations'
                        className={(navData) => navData.isActive ? "active-page" : ""}
                      >
                        Reservations
                      </NavLink>
                    </li>
                  </ul>
                </li>

                {['venue_owner', 'employee'].includes(user?.role) && (
                <li>
                  <NavLink
                    to='/calendar-main'
                    className={(navData) => navData.isActive ? "active-page" : ""}
                  >
                    <Icon icon='solar:calendar-outline' className='menu-icon' />
                    <span>Calendar</span>
                  </NavLink>
                </li>
                )}

                <li className='sidebar-menu-group-title'>Account</li>
                <li>
                  <Link
                    to='/applications/my-plan'
                    onClick={() => setActiveTabId('my-plan')}
                    className={
                      location.pathname === '/applications/my-plan' ||
                      (location.pathname === '/applications' && activeTabId === 'my-plan')
                        ? "active-page"
                        : ""
                    }
                  >
                    <Icon icon='solar:card-outline' className='menu-icon' />
                    <span>My Plan</span>
                  </Link>
                </li>
                <li>
                  <NavLink
                    to='/view-profile'
                    className={(navData) => navData.isActive || location.pathname.startsWith('/view-profile') ? "active-page" : ""}
                  >
                    <Icon icon='solar:user-circle-outline' className='menu-icon' />
                    <span>My Profile</span>
                  </NavLink>
                </li>

                <li className='sidebar-menu-group-title'>Legal</li>
                <li>
                  <NavLink
                    to='/tos'
                    className={(navData) => navData.isActive ? "active-page" : ""}
                  >
                    <Icon icon='solar:document-text-outline' className='menu-icon' />
                    <span>Terms of Service</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to='/privacy'
                    className={(navData) => navData.isActive ? "active-page" : ""}
                  >
                    <Icon icon='solar:shield-check-outline' className='menu-icon' />
                    <span>Privacy Policy</span>
                  </NavLink>
                </li>
              </>
            )}
          </ul>
        </div>
      </aside>

      <main
        className={sidebarActive ? "dashboard-main active" : "dashboard-main"}
      >
        <div className='navbar-header'>
          <div className='row align-items-center justify-content-between'>
            <div className='col-auto'>
              <div className='d-flex flex-wrap align-items-center gap-4'>
                <button
                  type='button'
                  className='sidebar-toggle'
                  onClick={sidebarControl}
                  aria-label='Toggle desktop sidebar'
                >
                  {sidebarActive ? (
                    <Icon
                      icon='iconoir:arrow-right'
                      className='icon text-2xl non-active'
                    />
                  ) : (
                    <Icon
                      icon='heroicons:bars-3-solid'
                      className='icon text-2xl non-active'
                    />
                  )}
                </button>
                <button
                  onClick={mobileMenuControl}
                  type='button'
                  className='sidebar-mobile-toggle'
                  aria-label='Toggle mobile menu'
                >
                  <Icon icon='heroicons:bars-3-solid' className='icon' />
                </button>
              </div>
            </div>
            <div className='col-auto'>
              <div className='d-flex flex-wrap align-items-center gap-3'>
                {/* ThemeToggleButton */}
                <ThemeToggleButton />
                
                {/* Notification dropdown start */}
                <div className='dropdown'>
                  <button
                    className='has-indicator w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center border-0'
                    type='button'
                    data-bs-toggle='dropdown'
                    aria-label='Notifications'
                  >
                    <Icon
                      icon='iconoir:bell'
                      className='text-primary-light text-xl'
                    />
                    {unreadCount > 0 && (
                      <span className='position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger'>
                        {unreadCount}
                        <span className='visually-hidden'>unread notifications</span>
                      </span>
                    )}
                  </button>
                  <div className='dropdown-menu to-top dropdown-menu-lg p-0'>
                    <div className='m-16 py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2'>
                      <div>
                        <h6 className='text-lg text-primary-light fw-semibold mb-0'>
                          Notifications
                        </h6>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                         <span className='text-primary-600 fw-semibold text-lg w-40-px h-40-px rounded-circle bg-base d-flex justify-content-center align-items-center'>
                          {unreadCount}
                        </span>
                        {unreadCount > 0 && (
                          <button 
                            onClick={handleMarkAllRead}
                            className="btn btn-sm btn-outline-primary"
                            style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                          >
                            Mark Read
                          </button>
                        )}
                      </div>
                    </div>
                    <div className='max-h-400-px overflow-y-auto scroll-sm pe-4'>
                      {notifications.length === 0 ? (
                        <div className="p-4 text-center text-secondary-light">
                          <Icon icon="solar:bell-linear" className="text-2xl mb-1 d-block mx-auto text-secondary" />
                          <span className="text-sm">No new notifications</span>
                        </div>
                      ) : (
                        notifications.map((notif) => (
                          <div
                            key={notif.id}
                            className={`px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between ${notif.is_read ? '' : 'bg-neutral-50'}`}
                          >
                            <div className='text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3'>
                              <span className={`w-44-px h-44-px rounded-circle d-flex justify-content-center align-items-center flex-shrink-0 ${notif.is_read ? 'bg-neutral-200 text-secondary-light' : 'bg-success-subtle text-success-main'}`}>
                                <Icon
                                  icon='solar:bell-bold'
                                  className='icon text-xl'
                                />
                              </span>
                              <div>
                                <h6 className='text-md fw-semibold mb-4'>
                                  {notif.title}
                                </h6>
                                <p className='mb-0 text-sm text-secondary-light text-w-200-px'>
                                  {notif.body}
                                </p>
                              </div>
                            </div>
                            <span className='text-sm text-secondary-light flex-shrink-0'>
                              {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                {/* Notification dropdown end */}

                {/* Profile dropdown start */}
                <div className='dropdown'>
                  <button
                    className='d-flex justify-content-center align-items-center rounded-circle border-0 bg-transparent'
                    type='button'
                    data-bs-toggle='dropdown'
                    aria-label='User Profile Menu'
                  >
                    <img
                      src={
                        user?.profile_image 
                          ? (user.profile_image.startsWith('http') 
                              ? user.profile_image 
                              : user.profile_image.includes('/avatar/') 
                                ? `${config.API_BASE_URL}${user.profile_image.startsWith('/') ? '' : '/'}${user.profile_image}`
                                : (user.profile_image.startsWith('/') ? user.profile_image : `/${user.profile_image}`))
                          : '/assets/images/users/user1.png'
                      }
                      alt='User avatar'
                      className='w-40-px h-40-px object-fit-cover rounded-circle bg-white'
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/assets/images/users/user1.png';
                      }}
                    />
                  </button>
                  <div className='dropdown-menu to-top dropdown-menu-sm'>
                    <div className='py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2'>
                      <div>
                        <h6 className='text-lg text-primary-light fw-semibold mb-2'>
                          {user?.full_name || 'User'}
                        </h6>
                        <span className='text-secondary-light fw-medium text-sm text-capitalize'>
                          {user?.role || 'Guest'}
                        </span>
                      </div>
                    </div>
                    <ul className='to-top-list'>
                      <li>
                        <Link
                          className='dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3'
                          to={user ? `/view-profile/${user.id}` : '/view-profile'}
                        >
                          <Icon
                            icon='solar:user-linear'
                            className='icon text-xl'
                          />{" "}
                          My Profile
                        </Link>
                      </li>
                      
                      <li>
                        <Link
                          className='dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3'
                          to='#'
                          onClick={(e) => {
                            e.preventDefault();
                            auth.logout();
                          }}
                        >
                          <Icon icon='lucide:power' className='icon text-xl' />{" "}
                          Log Out
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
                {/* Profile dropdown end */}
              </div>
            </div>
          </div>
        </div>

        {/* dashboard-main-body */}
        <div className='dashboard-main-body'>{children}</div>

        {/* Footer section */}
        <footer className='d-footer'>
          <div className='row align-items-center justify-content-between'>
            <div className='col-auto'>
              <p className='mb-0'>© 2026 HubScroll. All Rights Reserved.</p>
            </div>
            <div className='col-auto'>
              <p className='mb-0'>
                Powered by <span className='text-primary-600'>HubScroll</span>
              </p>
            </div>
          </div>
        </footer>
      </main>
    </section>
  );
};

export default MasterLayout;
